// frontend/src/store/templateStore.js
// Store de Zustand para gestionar el estado de los Tipos de Iniciativa y sus Plantillas.
// AHORA CON FUNCIONALIDAD CRUD.

import { create } from 'zustand';
import {
    getInitiativeTypes,
    getFullTemplateConfiguration,
    // Importar nuevas funciones CRUD
    createTemplatePhase,
    updateTemplatePhase,
    deleteTemplatePhase,
    createTemplateTask,
    updateTemplateTask,
    deleteTemplateTask,
    createDocumentSlot,
    updateDocumentSlot,
    deleteDocumentSlot,
} from '../api/template';
import { useAuthStore } from './authStore';
import { useUIStore } from './uiStore';

export const useTemplateStore = create((set, get) => ({
    initiativeTypes: [],
    fullConfiguration: [],
    isLoading: false,

    // --- ACCIONES DE LECTURA ---
    fetchInitiativeTypes: async () => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        set({ isLoading: true });
        try {
            const types = await getInitiativeTypes(token);
            set({ initiativeTypes: types });
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar los tipos de iniciativa.', 'error');
            set({ initiativeTypes: [] });
        } finally {
            set({ isLoading: false });
        }
    },

    fetchFullTemplateConfiguration: async () => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        set({ isLoading: true });
        try {
            const config = await getFullTemplateConfiguration(token);
            set({ fullConfiguration: config });
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar la configuración de plantillas.', 'error');
            set({ fullConfiguration: [] });
        } finally {
            set({ isLoading: false });
        }
    },

    // --- NUEVAS ACCIONES DE ESCRITURA (CRUD) ---

    // Operaciones para Fases
    addPhase: async (templateId, data) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await createTemplatePhase(templateId, data, token);
            await get().fetchFullTemplateConfiguration(); // Recargar datos
            useUIStore.getState().showTemporaryMessage('Fase creada con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al crear la fase.', 'error');
            throw error;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },
    updatePhase: async (phaseId, data) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await updateTemplatePhase(phaseId, data, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Fase actualizada con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar la fase.', 'error');
            throw error;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },
    deletePhase: async (phaseId) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await deleteTemplatePhase(phaseId, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Fase eliminada con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar la fase.', 'error');
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    // Operaciones para Tareas
    addTask: async (phaseId, data) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await createTemplateTask(phaseId, data, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Tarea creada con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al crear la tarea.', 'error');
            throw error;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },
    updateTask: async (taskId, data) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await updateTemplateTask(taskId, data, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Tarea actualizada con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar la tarea.', 'error');
            throw error;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },
    deleteTask: async (taskId) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await deleteTemplateTask(taskId, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Tarea eliminada con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar la tarea.', 'error');
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    // Operaciones para Documentos
    addDocumentSlot: async (initiativeTypeId, data) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await createDocumentSlot(initiativeTypeId, data, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Documento añadido con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al añadir el documento.', 'error');
            throw error;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },
    updateDocumentSlot: async (slotId, data) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await updateDocumentSlot(slotId, data, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Documento actualizado con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar el documento.', 'error');
            throw error;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },
    deleteDocumentSlot: async (slotId) => {
        const token = useAuthStore.getState().token;
        useUIStore.getState().setIsLoading(true);
        try {
            await deleteDocumentSlot(slotId, token);
            await get().fetchFullTemplateConfiguration();
            useUIStore.getState().showTemporaryMessage('Documento eliminado con éxito.', 'success');
        } catch (error) {
            useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar el documento.', 'error');
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },
}));
